import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { transmissionReportApiSlice } from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { setActiveColorCategory } from '@/store/slices/transmissionReports/transmissionReportsSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import { ReportColor } from '@/types/transmissionReport';

import { REPORT_LIMIT_MINI_MENU } from './useMiniMenuReportsInfinite';

const ALL_COLORS: ReportColor[] = [ReportColor.Red, ReportColor.Yellow, ReportColor.Green];

export const useReportListDrawer = () => {
  const dispatch = useAppDispatch();

  const { filters } = useAppSelector((state) => state.preferences);
  const { signatureStatus } = useAppSelector((state) => state.auth);
  const { selectedVendors } = useAppSelector((state) => state.transmissionReportForm);

  const { search } = useAppSelector((state) => state.transmissionReports);

  const filterValues = useMemo(() => {
    return {
      clinicIds: filters?.clinics.map((clinic) => clinic.value) ?? [],
      practitionerIds: filters?.practitioners.map((practitioner) => practitioner.value) ?? [],
      signingPractitionerIds:
        filters?.signingPractitioners?.map((signingPractitioner) => signingPractitioner.value) ??
        [],
      deviceTypes: filters?.deviceTypes.map((deviceType) => deviceType.value) ?? []
    };
  }, [filters]);

  const makeMiniMenuArgs = useCallback(
    (color: ReportColor) => ({
      ...filterValues,
      signatureStatus,
      search,
      color,
      limit: REPORT_LIMIT_MINI_MENU,
      vendors: selectedVendors
    }),
    [filterValues, signatureStatus, search, selectedVendors]
  );

  const makeReviewPendingArgs = useCallback(
    () => ({
      ...filterValues,
      signatureStatus,
      search,
      isPendingReview: true as const,
      limit: REPORT_LIMIT_MINI_MENU,
      offset: 0,
      vendors: selectedVendors
    }),
    [filterValues, signatureStatus, search, selectedVendors]
  );

  const baseFilterArgs = useMemo(
    () => ({
      ...filterValues,
      signatureStatus,
      search,
      limit: REPORT_LIMIT_MINI_MENU,
      vendors: selectedVendors
    }),
    [filterValues, signatureStatus, search, selectedVendors]
  );

  const removeReportFromMiniMenu = useCallback(
    (reportId: number, color: ReportColor) => {
      dispatch(
        transmissionReportApiSlice.util.updateQueryData(
          'getMiniMenuReports',
          makeMiniMenuArgs(color),
          (draft) => {
            draft.pages.forEach((page) => {
              page.data = page.data.filter((r) => r.id !== reportId);
            });
            if (draft.pages[0]) {
              draft.pages[0].total = Math.max(0, (draft.pages[0].total ?? 0) - 1);
            }
          }
        )
      );
    },
    [dispatch, makeMiniMenuArgs]
  );

  const removeReportFromReviewPending = useCallback(
    (reportId: number) => {
      dispatch(
        transmissionReportApiSlice.util.updateQueryData(
          'getReviewPendingReports',
          makeReviewPendingArgs(),
          (draft) => {
            // Remove the report from the data array
            const removedReport = draft.data.find((r) => r.id === reportId);
            draft.data = draft.data.filter((r) => r.id !== reportId);

            // Decrement the total count
            draft.total = Math.max(0, draft.total - 1);

            // Update colorCounts if they exist
            if (draft.colorCounts && removedReport?.color) {
              const colorCountIndex = draft.colorCounts.findIndex(
                (cc) => cc.color === removedReport.color
              );
              if (colorCountIndex !== -1) {
                draft.colorCounts[colorCountIndex].count = Math.max(
                  0,
                  draft.colorCounts[colorCountIndex].count - 1
                );
              }
            }
          }
        )
      );
    },
    [dispatch, makeReviewPendingArgs]
  );

  const colorBasedTotalCount = useAppSelector((state) =>
    ALL_COLORS.reduce((sum, color) => {
      const args = makeMiniMenuArgs(color);
      const result = transmissionReportApiSlice.endpoints.getMiniMenuReports.select(args)(state);
      const totalForColor = result.data?.pages?.[0]?.total ?? 0;
      return sum + totalForColor;
    }, 0)
  );

  const reviewPendingCount = useAppSelector((state) => {
    const args = makeReviewPendingArgs();
    const result = transmissionReportApiSlice.endpoints.getReviewPendingReports.select(args)(state);
    return result.data?.total ?? 0;
  });

  const totalCount = colorBasedTotalCount + reviewPendingCount;

  const moveReport = useCallback(
    (id: number, from_color: ReportColor, to_color: ReportColor) => {
      if (from_color === to_color) return;

      removeReportFromMiniMenu(id, from_color);

      dispatch(
        transmissionReportApiSlice.util.invalidateTags([
          { type: API_SLICE.TransmissionReport, id: `mini_menu_reports_${from_color}` },
          { type: API_SLICE.TransmissionReport, id: `mini_menu_reports_${to_color}` },
          { type: API_SLICE.TransmissionReport, id: 'mini_menu_reports' },
          { type: API_SLICE.TransmissionReport, id: 'paginated_reports' }
        ])
      );

      dispatch(setActiveColorCategory({ color: to_color }));
    },
    [dispatch, removeReportFromMiniMenu]
  );

  return {
    removeReportFromMiniMenu,
    removeReportFromReviewPending,
    totalCount,
    colorBasedTotalCount,
    reviewPendingCount,
    moveReport,
    baseFilterArgs,
    makeReviewPendingArgs
  };
};
